import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { ImagePlus, Info, Layers, Plus, Trash2, Upload } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { SummaryCard } from '@nucleus-commerce/admin-kit/forms';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { Media } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import {
  MediaPickerDialog,
  MediaThumb,
  useMediaUploader,
  type MediaUploadItem,
} from '@/features/media';

import type { ProductGalleryDraft } from '../hooks/use-product-gallery-draft';
import { useProductScope } from '../hooks/use-product-scope';
import { useVariants } from '../hooks/use-variants';

import { ProductMediaDetailsModal } from './product-media-details-modal';
import {
  GALLERY_TYPES,
  idsBetween,
  isGalleryFile,
  moveItem,
  moveToFront,
  removeItems,
  restoreRemoved,
  sameGallery,
  setVariantScope,
} from './product-media-gallery';
import { ProductMediaStaticTile, ProductMediaTile } from './product-media-tile';
import { ProductMediaUploadTile } from './product-media-upload-tile';
import { ProductMediaVariantsDialog } from './product-media-variants-dialog';
import { EditOnMasterLink, SharedChip } from './product-scope';

export interface ProductMediaSectionProps {
  /** The page's draft (`useProductGalleryDraft`); the page's Save writes it. */
  gallery: ProductGalleryDraft;
  /** A line above the gallery, e.g. that a store page edits the master gallery. */
  notice?: ReactNode;
}

const GRID = 'grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5';
const SHOWN_UPLOADS = new Set(['queued', 'uploading', 'error']);
const ACTIVE_UPLOADS = new Set(['queued', 'uploading']);

function Notice({ children }: { children: ReactNode }) {
  return (
    <p className="text-muted-foreground mb-3 flex items-start gap-2 text-xs">
      <Info className="mt-px size-3.5 shrink-0" aria-hidden />
      <span>{children}</span>
    </p>
  );
}

function GallerySkeleton() {
  return (
    <div className={GRID}>
      <Skeleton className="col-span-2 row-span-2 aspect-square" />
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="aspect-square" />
      ))}
    </div>
  );
}

/** The gallery on a store's product page: every store shows the same one, edited on master. */
function SharedGallery({ gallery, notice }: ProductMediaSectionProps) {
  const { t } = useTranslation('products');
  const { storeCount } = useProductScope();
  const { productId, query, items } = gallery;
  const count = items.length;
  const [detailId, setDetailId] = useState<string | null>(null);
  const detail = detailId ? items.find((i) => i.mediaId === detailId) : undefined;

  let body;
  if (query.isPending) body = <GallerySkeleton />;
  else if (query.error) {
    body = <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />;
  } else if (count === 0) {
    body = <p className="text-muted-foreground text-sm">{t('mediaDetails.emptyShared')}</p>;
  } else {
    body = (
      <ul className={GRID} aria-label={t('media.galleryLabel')}>
        {items.map((item, index) => (
          <ProductMediaStaticTile
            key={item.mediaId}
            item={item}
            position={index + 1}
            onOpen={(row) => setDetailId(row.mediaId)}
          />
        ))}
      </ul>
    );
  }

  return (
    <SummaryCard
      title={
        <>
          {t('media.title')}
          {count > 0 ? (
            <span className="text-muted-foreground font-normal">
              {' '}
              · {t('media.count', { count })}
            </span>
          ) : null}
        </>
      }
      badge={<SharedChip />}
      action={<EditOnMasterLink />}
      description={
        storeCount
          ? t('mediaDetails.sharedHint', { count: storeCount })
          : t('mediaDetails.sharedHintAny')
      }
    >
      {notice ? <Notice>{notice}</Notice> : null}
      {body}
      {detail ? (
        <ProductMediaDetailsModal
          productId={productId}
          item={detail}
          siblings={items.map((i) => i.media)}
          featured={items[0]?.mediaId === detail.mediaId}
          canEdit={false}
          canEditFile={false}
          onNavigate={(media) => setDetailId(media.id)}
          onClose={() => setDetailId(null)}
          onApply={() => undefined}
          onRemove={() => undefined}
        />
      ) : null}
    </SummaryCard>
  );
}

export function ProductMediaSection(props: ProductMediaSectionProps) {
  const { readOnly } = useProductScope();
  return readOnly ? <SharedGallery {...props} /> : <GalleryEditor {...props} />;
}

/**
 * The product's gallery, Shopify style: the first file is featured and shown large, tiles are
 * dragged to reorder, files dropped anywhere on the card upload into it, and ticked files get
 * bulk actions. Changes edit the page's draft and are written when the page saves; uploads go
 * to the media library at once, but join the product only with that save.
 */
function GalleryEditor({ gallery, notice }: ProductMediaSectionProps) {
  const { t } = useTranslation('products');
  const { can } = usePermissions();
  const { shared, storeCount } = useProductScope();
  const canEdit = can('products.write');
  const canUpload = canEdit && can('media.write');

  const { productId, query, items, latest, dirty, saving } = gallery;
  const variantsQuery = useVariants(productId);
  // `change` and `add` are stable; the upload queue is rebuilt whenever its callback changes.
  const { change, add } = gallery;

  // Selection
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const anchor = useRef<string | null>(null);
  const selectedIds = useMemo(
    () => new Set(items.filter((i) => selected.has(i.mediaId)).map((i) => i.mediaId)),
    [items, selected],
  );
  const selecting = selectedIds.size > 0;
  const clearSelection = () => {
    setSelected(new Set());
    anchor.current = null;
  };
  const toggleSelect = (id: string, range: boolean) => {
    // Read the anchor now: the updater runs later, after it has moved to this tile.
    const from = range ? anchor.current : null;
    const gallery = latest.current;
    setSelected((prev) => {
      const next = new Set(prev);
      if (from) {
        const add = !prev.has(id);
        for (const rangeId of idsBetween(gallery, from, id)) {
          if (add) next.add(rangeId);
          else next.delete(rangeId);
        }
      } else if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    anchor.current = id;
  };

  // Changes
  const onUploaded = useCallback((media: Media) => add([media]), [add]);
  const uploads = useMediaUploader({ onUploaded });
  const activeUploads = uploads.items.filter((u) => ACTIVE_UPLOADS.has(u.status)).length;
  const { onUploadsChange } = gallery;
  useEffect(() => onUploadsChange?.(activeUploads), [onUploadsChange, activeUploads]);

  const remove = (ids: ReadonlySet<string>) => {
    const before = latest.current;
    change(removeItems(before, ids));
    setSelected((prev) => new Set([...prev].filter((id) => !ids.has(id))));
    toast(t('media.removed', { count: ids.size }), {
      action: {
        label: t('media.undo'),
        onClick: () => change(restoreRemoved(latest.current, before, ids)),
      },
    });
  };

  // Files from the computer: the header button, the add tile, or a drop on the card.
  const fileInput = useRef<HTMLInputElement>(null);
  const addFiles = (files: File[]) => {
    const accepted = files.filter(isGalleryFile);
    const skipped = files.length - accepted.length;
    if (skipped > 0) toast.error(t('media.skipped', { count: skipped }));
    if (accepted.length > 0) uploads.add(accepted);
  };
  const retry = (upload: MediaUploadItem) => {
    uploads.remove(upload.id);
    uploads.add([upload.file]);
  };
  const [dropping, setDropping] = useState(false);
  const dragDepth = useRef(0);
  const carriesFiles = (e: DragEvent) => e.dataTransfer.types.includes('Files');
  const dropHandlers = canUpload
    ? {
        onDragEnter: (e: DragEvent) => {
          if (!carriesFiles(e)) return;
          e.preventDefault();
          dragDepth.current += 1;
          setDropping(true);
        },
        onDragOver: (e: DragEvent) => {
          if (!carriesFiles(e)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        },
        onDragLeave: (e: DragEvent) => {
          if (!carriesFiles(e)) return;
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDropping(false);
        },
        onDrop: (e: DragEvent) => {
          if (!carriesFiles(e)) return;
          e.preventDefault();
          dragDepth.current = 0;
          setDropping(false);
          addFiles(Array.from(e.dataTransfer.files));
        },
      }
    : {};

  // Reordering
  const sensors = useSensors(
    // A few pixels of movement before a drag starts, so a click still opens the file.
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    // Touch waits for a press so the page still scrolls.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      // Enter opens the file; Space picks it up.
      keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space', 'Enter'] },
    }),
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const active = activeId ? items.find((i) => i.mediaId === activeId) : undefined;
  const label = (id: string | number) => {
    const row = items.find((i) => i.mediaId === id);
    return row?.media.filename ?? row?.media.alt ?? t('media.untitled');
  };
  const position = (id: string | number) => items.findIndex((i) => i.mediaId === id) + 1;
  const announcements: Announcements = {
    onDragStart: ({ active: a }) =>
      t('media.dnd.picked', { name: label(a.id), position: position(a.id), count: items.length }),
    onDragOver: ({ active: a, over }) =>
      over
        ? t('media.dnd.over', {
            name: label(a.id),
            position: position(over.id),
            count: items.length,
          })
        : undefined,
    onDragEnd: ({ active: a, over }) =>
      over
        ? t('media.dnd.dropped', {
            name: label(a.id),
            position: position(over.id),
            count: items.length,
          })
        : t('media.dnd.cancelled', { name: label(a.id) }),
    onDragCancel: ({ active: a }) => t('media.dnd.cancelled', { name: label(a.id) }),
  };
  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    if (!e.over || e.active.id === e.over.id) return;
    change(moveItem(latest.current, String(e.active.id), String(e.over.id)));
  };

  // Dialogs
  const [pickerOpen, setPickerOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const detail = detailId ? items.find((i) => i.mediaId === detailId) : undefined;
  const [scopeTargets, setScopeTargets] = useState<ReadonlySet<string> | null>(null);
  const scopeRows = scopeTargets ? items.filter((i) => scopeTargets.has(i.mediaId)) : [];
  const firstScope = scopeRows[0]?.variantIds ?? [];
  const scopeMixed = scopeRows.some(
    (r) => [...r.variantIds].sort().join() !== [...firstScope].sort().join(),
  );

  const visibleUploads = uploads.items.filter((u) => SHOWN_UPLOADS.has(u.status));
  const count = items.length;
  const allSelected = selecting && selectedIds.size === count;

  const header = selecting ? (
    <div className="flex min-h-9 flex-1 flex-wrap items-center gap-3">
      <Checkbox
        checked={allSelected ? true : 'indeterminate'}
        aria-label={allSelected ? t('media.deselectAll') : t('media.selectAll')}
        onCheckedChange={() =>
          allSelected ? clearSelection() : setSelected(new Set(items.map((i) => i.mediaId)))
        }
      />
      <span className="text-sm font-medium" aria-live="polite">
        {t('media.selected', { count: selectedIds.size })}
      </span>
      <div className="ml-auto flex flex-wrap gap-2">
        {productId ? (
          <Button variant="outline" size="sm" onClick={() => setScopeTargets(selectedIds)}>
            <Layers aria-hidden />
            {t('media.variantsAction')}
          </Button>
        ) : null}
        <Button
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={() => remove(selectedIds)}
        >
          <Trash2 aria-hidden />
          {t('media.removeSelected', { count: selectedIds.size })}
        </Button>
        <Button variant="ghost" size="sm" onClick={clearSelection}>
          {t('media.clearSelection')}
        </Button>
      </div>
    </div>
  ) : (
    <>
      <div className="grid gap-0.5">
        <CardTitle className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[15px]">
          <span>
            {t('media.title')}
            {count > 0 ? (
              <span className="text-muted-foreground font-normal">
                {' '}
                · {t('media.count', { count })}
              </span>
            ) : null}
          </span>
          <SharedChip />
          {dirty ? (
            <span className="border-warning/50 bg-warning/20 rounded-full border px-1.5 py-0.5 align-middle text-[10px] font-medium">
              {t('media.unsaved')}
            </span>
          ) : null}
        </CardTitle>
        <p className="text-muted-foreground text-[13px]">
          {shared
            ? storeCount
              ? t('mediaDetails.sharedHint', { count: storeCount })
              : t('mediaDetails.sharedHintAny')
            : t('media.hint')}
        </p>
      </div>
      {canEdit && count > 0 ? (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
            <ImagePlus aria-hidden />
            {t('media.fromLibrary')}
          </Button>
          {canUpload ? (
            <Button size="sm" onClick={() => fileInput.current?.click()}>
              <Upload aria-hidden />
              {t('media.upload')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </>
  );

  let body;
  if (query.isPending) {
    body = <GallerySkeleton />;
  } else if (query.error) {
    body = <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />;
  } else if (count === 0 && visibleUploads.length === 0) {
    body = (
      <div
        className="flex flex-col items-center gap-3 rounded-lg border border-dashed px-6 py-10 text-center"
        data-tour="catalog.product-edit.media-empty"
      >
        <ImagePlus className="text-muted-foreground size-8" aria-hidden />
        <p className="text-muted-foreground max-w-sm text-sm">
          {canUpload ? t('media.emptyDrop') : t('media.empty')}
        </p>
        {canEdit ? (
          <div className="flex flex-wrap justify-center gap-2">
            {canUpload ? (
              <Button size="sm" onClick={() => fileInput.current?.click()}>
                <Upload aria-hidden />
                {t('media.uploadNew')}
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
              {t('media.selectExisting')}
            </Button>
          </div>
        ) : null}
        <p className="text-muted-foreground text-xs">{t('media.accepts')}</p>
      </div>
    );
  } else {
    body = (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setActiveId(null)}
        accessibility={{
          announcements,
          screenReaderInstructions: { draggable: t('media.dnd.instructions') },
        }}
      >
        <SortableContext items={items.map((i) => i.mediaId)} strategy={rectSortingStrategy}>
          <ul className={GRID} aria-label={t('media.galleryLabel')}>
            {items.map((item, index) => (
              <ProductMediaTile
                key={item.mediaId}
                item={item}
                position={index + 1}
                count={count}
                selected={selectedIds.has(item.mediaId)}
                selecting={selecting}
                canEdit={canEdit}
                onToggleSelect={toggleSelect}
                onOpen={(row) => setDetailId(row.mediaId)}
                onMakeFeatured={(id) => change(moveToFront(latest.current, id))}
                onVariants={productId ? (id) => setScopeTargets(new Set([id])) : undefined}
                onRemove={(id) => remove(new Set([id]))}
              />
            ))}
            {visibleUploads.map((upload) => (
              <ProductMediaUploadTile
                key={upload.id}
                upload={upload}
                onCancel={uploads.cancel}
                onRemove={uploads.remove}
                onRetry={retry}
              />
            ))}
            {canUpload ? (
              <li>
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  className="text-muted-foreground hover:border-primary/60 hover:text-foreground focus-visible:ring-ring/50 flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed text-xs outline-none focus-visible:ring-[3px]"
                >
                  <Plus className="size-5" aria-hidden />
                  {t('media.addTile')}
                </button>
              </li>
            ) : null}
          </ul>
        </SortableContext>
        <DragOverlay>
          {active ? (
            <MediaThumb
              media={active.media}
              rendition="medium"
              className="size-full cursor-grabbing rounded-lg border shadow-xl"
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    );
  }

  return (
    <Card
      className="relative gap-2.5 py-4"
      data-tour="catalog.product-edit.media"
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === 'Escape' && selecting && !activeId) clearSelection();
      }}
      {...dropHandlers}
    >
      <CardHeader className="flex flex-row items-start justify-between gap-3 px-4">
        {header}
      </CardHeader>
      <CardContent className={cn('px-4', saving && 'cursor-progress')}>
        {notice ? <Notice>{notice}</Notice> : null}
        {body}
      </CardContent>

      {dropping ? (
        <div
          className="border-primary bg-primary/5 pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed backdrop-blur-[1px]"
          aria-hidden
        >
          <Upload className="text-primary size-7" />
          <p className="text-sm font-medium">{t('media.dropHere')}</p>
        </div>
      ) : null}

      <input
        ref={fileInput}
        type="file"
        multiple
        accept={GALLERY_TYPES.join(',')}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        data-testid="product-media-file-input"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length > 0) addFiles(files);
        }}
      />
      <MediaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        attachedIds={new Set(items.map((i) => i.mediaId))}
        attachedLabel={t('mediaDetails.onThisProduct')}
        onPick={add}
      />
      {detail ? (
        <ProductMediaDetailsModal
          productId={productId}
          item={detail}
          siblings={items.map((i) => i.media)}
          featured={items[0]?.mediaId === detail.mediaId}
          canEdit={canEdit}
          canEditFile={can('media.write')}
          onMediaUpdated={gallery.refreshMedia}
          onNavigate={(media) => setDetailId(media.id)}
          onClose={() => setDetailId(null)}
          onApply={(variantIds, makeFeatured) => {
            const ids = new Set([detail.mediaId]);
            const scoped = setVariantScope(latest.current, ids, variantIds);
            const next = makeFeatured ? moveToFront(scoped, detail.mediaId) : scoped;
            if (!sameGallery(next, latest.current)) change(next);
          }}
          onRemove={() => {
            setDetailId(null);
            remove(new Set([detail.mediaId]));
          }}
        />
      ) : null}
      {scopeTargets ? (
        <ProductMediaVariantsDialog
          fileCount={scopeTargets.size}
          initial={scopeMixed ? [] : firstScope}
          mixed={scopeMixed}
          variants={variantsQuery.data ?? []}
          onClose={() => setScopeTargets(null)}
          onSave={(variantIds) => change(setVariantScope(latest.current, scopeTargets, variantIds))}
        />
      ) : null}
    </Card>
  );
}
